/**
 * Centralized API client with automatic token refresh
 * Implements OAuth 2.0 token refresh pattern
 *
 * Features:
 * - Automatic token refresh on 401 responses
 * - Circuit breaker to prevent refresh storms
 * - Request queuing during refresh to prevent multiple simultaneous refreshes
 * - Exponential backoff on failures
 */

import { getAuthToken, setAuthToken, clearAuthTokens } from './auth-utils';
import { isDevelopment } from '@/config/environment';
import { logFailedTokenRefresh, logCircuitBreakerTrip } from './security-monitor';

// Import RequestInit type from DOM library for proper typing
type RequestInitOptions = globalThis.RequestInit;

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let refreshSubscribers: Array<(_token: string | null) => void> = [];

// Circuit breaker state
let refreshFailureCount = 0;
let lastRefreshAttempt = 0;
let lastSuccessfulRefresh = 0;
const MAX_REFRESH_FAILURES = 3;
const MIN_REFRESH_INTERVAL = 5000; // 5 seconds between refresh attempts
const FAILURE_DECAY_INTERVAL = 300000; // 5 minutes - gradual decay of failure count

/**
 * Subscribe to token refresh completion
 */
function subscribeTokenRefresh(callback: (_token: string | null) => void) {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that the refresh attempt finished.
 * token is null when the refresh failed — subscribers MUST still be flushed,
 * otherwise their pending requests hang forever (eternal loading skeletons).
 */
function onTokenRefreshed(token: string | null) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

/**
 * Reset circuit breaker state (call after successful login)
 * Allows token refresh to work again after previous failures
 */
export function resetCircuitBreaker(): void {
  refreshFailureCount = 0;
  lastRefreshAttempt = 0;
  lastSuccessfulRefresh = Date.now();
  // Removed console.log for production security (no token timing information leakage)
}

/**
 * Gradual decay of failure count over time
 * Instead of keeping failures forever, they decay after successful periods
 */
function applyFailureDecay(): void {
  if (refreshFailureCount === 0 || lastSuccessfulRefresh === 0) {
    return;
  }

  const timeSinceSuccess = Date.now() - lastSuccessfulRefresh;

  // For every 5 minutes of successful operation, reduce failure count by 1
  const decayPeriods = Math.floor(timeSinceSuccess / FAILURE_DECAY_INTERVAL);

  if (decayPeriods > 0) {
    const previousCount = refreshFailureCount;
    refreshFailureCount = Math.max(0, refreshFailureCount - decayPeriods);

    if (previousCount !== refreshFailureCount && !isDevelopment) {
      // Log failure count decay in development only
    }

    // Update last success time to prevent immediate re-decay
    lastSuccessfulRefresh = Date.now();
  }
}

/**
 * Refresh the access token using refresh token
 * Implements circuit breaker pattern to prevent refresh storms
 *
 * SECURITY: Refresh token is sent via httpOnly cookie (automatic, secure)
 * No need to manually include it in request body
 */
async function refreshAccessToken(): Promise<string | null> {
  // Apply gradual decay to failure count (forgive old failures)
  applyFailureDecay();

  // Circuit breaker: Check if we're in a failure state
  if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
    console.error('Token refresh circuit breaker tripped. Too many failures. Please log in again.');
    logCircuitBreakerTrip(refreshFailureCount);
    clearAuthTokens();

    // Only redirect in production (dev mode might not have proper auth)
    if (!isDevelopment && window.location.pathname !== '/auth') {
      window.location.href = '/sign-in';
    }
    return null;
  }

  // Circuit breaker: Enforce minimum interval between attempts
  const now = Date.now();
  const timeSinceLastAttempt = now - lastRefreshAttempt;
  const requiredInterval = MIN_REFRESH_INTERVAL * Math.pow(2, refreshFailureCount); // Exponential backoff

  if (timeSinceLastAttempt < requiredInterval) {
    console.warn(
      `Token refresh rate limited. Please wait ${Math.ceil((requiredInterval - timeSinceLastAttempt) / 1000)}s`,
    );
    return null;
  }

  // Record attempt timestamp
  lastRefreshAttempt = Date.now();

  try {
    // SECURITY: Refresh token is automatically sent via httpOnly cookie
    // No need to include it in request body - more secure (XSS protection)
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // CRITICAL: Include cookies in request
    });

    if (!response.ok) {
      refreshFailureCount++;

      // Refresh token is invalid or expired

      console.error(
        `Token refresh failed: ${response.status} (failure ${refreshFailureCount}/${MAX_REFRESH_FAILURES})`,
      );
      logFailedTokenRefresh(response.status);

      // If we've hit max failures, clear tokens and redirect
      if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
        console.warn('Max refresh failures reached. Clearing tokens.');
        logCircuitBreakerTrip(refreshFailureCount);
        clearAuthTokens();

        // Only redirect in production
        if (!isDevelopment && window.location.pathname !== '/auth') {
          window.location.href = '/sign-in';
        }
      }

      return null;
    }

    const data = await response.json();

    // SECURITY: Server sets new refresh token as httpOnly cookie
    // We only need to store the access token
    if (data.accessToken) {
      setAuthToken(data.accessToken);
      refreshFailureCount = 0; // Reset failure count on success
      lastSuccessfulRefresh = Date.now(); // Track successful refresh for decay
      // Removed console.log for production security (no token timing information leakage)
      return data.accessToken;
    }

    return null;
  } catch (error) {
    refreshFailureCount++;

    console.error(
      `Error refreshing token (failure ${refreshFailureCount}/${MAX_REFRESH_FAILURES}):`,
      error,
    );
    logFailedTokenRefresh(undefined, error instanceof Error ? error.message : 'Unknown error');

    // If we've hit max failures, clear tokens
    if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
      logCircuitBreakerTrip(refreshFailureCount);
      clearAuthTokens();
    }

    return null;
  }
}

/**
 * Enhanced fetch with automatic token refresh on 401
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInitOptions = {},
): Promise<Response> {
  // Add auth token to request
  const token = getAuthToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Make initial request
  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If 401 Unauthorized, try to refresh token and retry
  if (response.status === 401 && !url.includes('/api/auth/')) {
    // If already refreshing, wait for it to complete
    if (isRefreshing) {
      return new Promise(resolve => {
        subscribeTokenRefresh(async newToken => {
          if (!newToken) {
            // Refresh failed — resolve with the original 401 instead of hanging
            resolve(response);
            return;
          }
          const retryHeaders = new Headers(options.headers);
          retryHeaders.set('Authorization', `Bearer ${newToken}`);

          const retryResponse = await fetch(url, {
            ...options,
            headers: retryHeaders,
          });

          resolve(retryResponse);
        });
      });
    }

    // Start refresh process
    isRefreshing = true;

    const newToken = await refreshAccessToken();
    isRefreshing = false;

    // Always flush the waiting queue — null lets queued requests settle on their 401
    onTokenRefreshed(newToken);

    if (newToken) {
      // Retry original request with new token
      const retryHeaders = new Headers(options.headers);
      retryHeaders.set('Authorization', `Bearer ${newToken}`);

      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });
    } else if (!isDevelopment && window.location.pathname !== '/auth') {
      window.location.href = '/sign-in';
    }
  }

  return response;
}

/**
 * Convenience methods for common HTTP verbs
 */
export const apiClient = {
  async get(url: string, options?: RequestInitOptions) {
    return fetchWithAuth(url, { ...options, method: 'GET' });
  },

  async post(url: string, data?: unknown, options?: RequestInitOptions) {
    return fetchWithAuth(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async put(url: string, data?: unknown, options?: RequestInitOptions) {
    return fetchWithAuth(url, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async delete(url: string, options?: RequestInitOptions) {
    return fetchWithAuth(url, { ...options, method: 'DELETE' });
  },

  async patch(url: string, data?: unknown, options?: RequestInitOptions) {
    return fetchWithAuth(url, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },
};

/**
 * Logout function that revokes refresh token
 *
 * SECURITY: Refresh token is sent via httpOnly cookie (automatic)
 * Server clears the cookie on logout
 */
export async function logout() {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // CRITICAL: Include cookies in request
    });
  } catch (error) {
    console.error('Error during logout:', error);
  }

  // Clear access token from localStorage
  clearAuthTokens();

  // Broadcast logout to other tabs
  import('./token-sync')
    .then(({ tokenSync }) => {
      tokenSync.broadcastLogout();
    })
    .catch(() => {
      // Silent fail if token-sync module not available
    });

  window.location.href = '/sign-in';
}
